import { useCallback, useMemo } from "react"

import type { DateTime } from "@bounce/date"

import { getDateTime, getJsDate } from "../helpers/date"
import { intlDateTimeFormat } from "../helpers/intlDateTimeFormat"
import { useTranslation } from "../libs/react-i18next"

export const useDateTimeFormatter = ({ options }: { options: Intl.DateTimeFormatOptions }) => {
  const { i18n } = useTranslation()

  return intlDateTimeFormat({ locale: i18n.language, options }).format
}

export const useFormatIsoDate = () => useCallback((date: DateTime | string) => getDateTime(date).toISODate(), [])

export const useFormatRelativeDate = () => {
  const {
    i18n: { language },
  } = useTranslation()
  const dateTimeFormat = useDateTimeFormatter({ options: { month: "long", year: "numeric" } })

  const oneMonthAgo = useMemo(() => {
    const now = new Date()
    const oneMonthAgo = new Date()
    oneMonthAgo.setMonth(now.getMonth() - 1)

    return oneMonthAgo
  }, [])

  return useCallback(
    (date: DateTime | string) => {
      const jsDate = getJsDate(date)
      const isAtLeastOneMonthAgo = new Date(jsDate) < oneMonthAgo

      return isAtLeastOneMonthAgo
        ? dateTimeFormat(jsDate)
        : getDateTime(date).toRelative({
            locale: language,
            unit: ["weeks", "days", "hours", "minutes"],
          })
    },
    [dateTimeFormat, language, oneMonthAgo],
  )
}
